import React, { useState } from "react";

import clubbieLogo from "../../assets/images/clubbie_logo.svg";
import stripeIcon from "../../assets/images/stripe_icon.svg";
import { initiateUserStripeConnect } from "../../redux/actions/stripeActions";
import { DASHBOARD_STACK } from "../../utils/screenNames";

const Benefit = ({ title, description, icon }) => (
  <div className="flex items-start gap-3">
    <div className="mt-0.5 text-[#00E7FF]">{icon}</div>
    <div className="flex flex-col">
      <p className="text-[#FEFCEB] text-[15px] font-medium">{title}</p>
      <p className="text-app-text-secondary text-[13px] mt-0.5">{description}</p>
    </div>
  </div>
);

const QuickSetupIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
  </svg>
);

const SecureIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    <polyline points="9 12 11 14 15 10" />
  </svg>
);

const GlobeIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <line x1="2" y1="12" x2="22" y2="12" />
    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
  </svg>
);

const TransferArrows = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#7F8585" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="17 1 21 5 17 9" />
    <path d="M3 11V9a4 4 0 0 1 4-4h14" />
    <polyline points="7 23 3 19 7 15" />
    <path d="M21 13v2a4 4 0 0 1-4 4H3" />
  </svg>
);

const SyncStripeAccount = ({ onConnected }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleConnectStripe = async () => {
    setError("");
    setLoading(true);
    try {
      const origin = window.location.origin;
      const refreshURL = `${origin}${DASHBOARD_STACK.STRIPE_CONNECT_REFRESH}`;
      const returnURL = `${origin}${DASHBOARD_STACK.STRIPE_CONNECT_RETURN}`;

      const data = await initiateUserStripeConnect({ refreshURL, returnURL });
      if (!data?.url) {
        throw new Error("No onboarding URL returned");
      }
      window.location.href = data.url;
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Could not start Stripe onboarding. Please try again."
      );
      setLoading(false);
    }
  };

  return (
    <div className="flex w-full flex-1 min-h-0 flex-col items-center justify-start text-center px-4">
      <div className="flex flex-col items-center w-full max-w-[420px]">
        <h2 className="text-[20px] font-medium text-[#FEFCEB] mb-2">
          Let's sync your Stripe account
        </h2>
        <p className="text-app-text-secondary text-[14px] mb-8">
          By doing so, you'll be able to:
        </p>

        <div className="flex items-center justify-center gap-4 mb-10">
          <img src={clubbieLogo} alt="Clubbie" className="w-16 h-16" />
          <TransferArrows />
          <img src={stripeIcon} alt="Stripe" className="w-16 h-16" />
        </div>

        <div className="flex flex-col gap-5 w-full mb-10 text-left">
          <Benefit
            icon={<QuickSetupIcon />}
            title="Quick setup"
            description="Connect your account and set up payments in minutes."
          />
          <Benefit
            icon={<SecureIcon />}
            title="Secure payments"
            description="All data is encrypted and you're always in control."
          />
          <Benefit
            icon={<GlobeIcon />}
            title="Global support"
            description="Accept 135+ currencies and payment methods worldwide."
          />
        </div>

        {error && (
          <p className="text-red-400 text-sm mb-3">{error}</p>
        )}

        <button
          onClick={handleConnectStripe}
          disabled={loading}
          className="w-full h-11 bg-[#00E7FF] text-[#021529] rounded-full font-medium text-[15px] hover:opacity-50 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {loading ? "Redirecting..." : (
            <>
              Connect to Stripe
              <span>›</span>
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default SyncStripeAccount;
